import React, { useState, useMemo, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { format, parseISO, subDays, isAfter } from 'date-fns';
import {
  Activity,
  Scale,
  Droplets,
  HeartPulse,
  Wind,
  Thermometer,
  SlidersHorizontal,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  X,
  Utensils,
} from 'lucide-react';
import { useData } from '../context/DataContext';
import { cn } from '../lib/utils';
import { formatWeight } from '../functions/formatWeight';
import { formatFluid } from '../functions/formatFluid';
import { formatTemp } from '../functions/formatTemp';
import { useSettings } from '../functions/useSettings';
import { useLanguage } from '../functions/useLanguage';
import { getDateLocale } from '../functions/getDateLocale';
import { formatDateByPreference } from '../functions/formatDateByPreference';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useTrendConfig, type WidgetId } from '../hooks/useTrendConfig';
import { Dropdown } from '../components/Dropdown';
import MucousMembraneIcon from '../components/Custom Icons/MucousMembraneIcon';
import type { DailyLog } from '../types';

type MucousMembraneValue = NonNullable<DailyLog['mucous_membrane']>;

const MUCUS_MEMBRANE_META: Record<
  MucousMembraneValue,
  {
    labelKey:
      | 'trends.mucousMembrane.pink'
      | 'trends.mucousMembrane.red'
      | 'trends.mucousMembrane.blue'
      | 'trends.mucousMembrane.white';
    fill: string;
  }
> = {
  pink: { labelKey: 'trends.mucousMembrane.pink', fill: '#f472b6' },
  red: { labelKey: 'trends.mucousMembrane.red', fill: '#ef4444' },
  blue: { labelKey: 'trends.mucousMembrane.blue', fill: '#3b82f6' },
  white: { labelKey: 'trends.mucousMembrane.white', fill: '#f3f4f6' },
};

const MUCUS_MEMBRANE_ORDER: MucousMembraneValue[] = ['pink', 'red', 'blue', 'white'];

// ─── Main component ──────────────────────────────────────────────────────────

export default function Trends() {
  const { logs } = useData();
  const { t, language } = useLanguage();
  const { isMetric, dateFormat } = useSettings();
  const { plan } = useFeatureAccess();
  const { widgets, isCustomisable, moveWidget, toggleWidget, resetToDefaults } =
    useTrendConfig(plan);

  const [range, setRange] = useState<'7d' | '14d' | '30d'>('30d');
  const [showCustomise, setShowCustomise] = useState(false);

  // Delay chart rendering by one animation frame so ResponsiveContainer
  // measures a non-zero width after the DOM has painted (fixes mobile blank charts).
  const [chartsReady, setChartsReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setChartsReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const dateLocale = useMemo(() => getDateLocale(language), [language]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const formatChartTick = (dateStr: string) => {
    const date = parseISO(dateStr);
    switch (dateFormat) {
      case 'DD/MM/YYYY':
      case 'DD/Month/YYYY':
        return format(date, 'd MMM', { locale: dateLocale });
      case 'YYYY-MM-DD':
        return format(date, 'MM-dd');
      default:
        return format(date, 'MMM d', { locale: dateLocale });
    }
  };

  // Group logs by date
  const logsByDate = logs.reduce<Record<string, typeof logs>>((acc, log) => {
    const date = format(parseISO(log.timestamp), 'yyyy-MM-dd');
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {} as Record<string, typeof logs>);

  // One data point per day
  const chartData = (Object.entries(logsByDate) as [string, typeof logs][])
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, dayLogs]) => {
      const weightValues = dayLogs
        .filter(l => l.weight_kg != null)
        .map(l => l.weight_kg as number);
      const avgWeight =
        weightValues.length > 0
          ? weightValues.reduce((a, b) => a + b, 0) / weightValues.length
          : null;
      const totalFluid = dayLogs.reduce((s, l) => s + (l.fluid_in_ml || 0), 0);
      const totalFood = dayLogs.reduce((s, l) => s + (l.food_in_mg || 0), 0);

      const hrLog = dayLogs.find(l => l.heart_rate_bpm != null);
      const rrLog = dayLogs.find(l => l.resp_rate_bpm != null);
      const tempLog = dayLogs.find(l => l.temp_c != null);
      const mucousMembraneLog = dayLogs.find(l => l.mucous_membrane != null);

      return {
        date,
        weight: avgWeight,
        heartRate: hrLog?.heart_rate_bpm ?? null,
        respRate: rrLog?.resp_rate_bpm ?? null,
        fluid: totalFluid || null,
        food: totalFood || null,
        temp: tempLog?.temp_c ?? null,
        mucousMembrane: mucousMembraneLog?.mucous_membrane ?? null,
      };
    });

  // Range filter
  const cutoff = subDays(new Date(), range === '7d' ? 7 : range === '14d' ? 14 : 30);
  const filteredData = chartData.filter(d => isAfter(parseISO(d.date), cutoff));
  const filteredUniqueDates = filteredData.length;

  // Stats
  const hrReadings = filteredData.filter(d => d.heartRate != null).map(d => d.heartRate as number);
  const avgHR = hrReadings.length > 0
    ? Math.round(hrReadings.reduce((a, b) => a + b, 0) / hrReadings.length)
    : null;
  const rrReadings = filteredData.filter(d => d.respRate != null).map(d => d.respRate as number);
  const avgRR = rrReadings.length > 0
    ? Math.round(rrReadings.reduce((a, b) => a + b, 0) / rrReadings.length)
    : null;
  const weightReadings = filteredData.filter(d => d.weight != null).map(d => d.weight as number);
  const avgWeight = weightReadings.length > 0
    ? weightReadings.reduce((a, b) => a + b, 0) / weightReadings.length
    : null;
  const fluidReadings = filteredData.filter(d => d.fluid != null).map(d => d.fluid as number);
  const avgFluid = fluidReadings.length > 0
    ? fluidReadings.reduce((a, b) => a + b, 0) / fluidReadings.length
    : null;
  const tempReadings = filteredData.filter(d => d.temp != null).map(d => d.temp as number);
  const avgTemp = tempReadings.length > 0
    ? Math.round((tempReadings.reduce((a, b) => a + b, 0) / tempReadings.length) * 100) / 100
    : null;
  const foodReadings = filteredData.filter(d => d.food != null).map(d => d.food as number);
  const avgFood = foodReadings.length > 0
    ? foodReadings.reduce((a, b) => a + b, 0) / foodReadings.length
    : null;
  const mucousReadings = filteredData
    .map(d => d.mucousMembrane)
    .filter((v): v is MucousMembraneValue => v != null);
  const avgMucousMembrane: MucousMembraneValue | null = mucousReadings.length > 0
    ? MUCUS_MEMBRANE_ORDER.reduce((best, value) => {
      const bestCount = mucousReadings.filter(v => v === best).length;
      const valueCount = mucousReadings.filter(v => v === value).length;
      return valueCount > bestCount ? value : best;
    }, MUCUS_MEMBRANE_ORDER[0])
    : null;
  const mucousMembraneData = MUCUS_MEMBRANE_ORDER
    .map(value => {
      const count = filteredData.filter(d => d.mucousMembrane === value).length;
      return {
        key: value,
        label: t(MUCUS_MEMBRANE_META[value].labelKey),
        value: count,
        fill: MUCUS_MEMBRANE_META[value].fill,
      };
    })
    .filter(entry => entry.value > 0);

  // ── No logs at all ────────────────────────────────────────────────────────

  if (logs.length === 0) {
    // Generate a convincing fake sine-wave dataset for the ghost charts
    const ghostData = Array.from({ length: 14 }, (_, i) => {
      const t2 = i / 13;
      return {
        date: format(subDays(new Date(), 13 - i), 'yyyy-MM-dd'),
        weight: 4.2 + Math.sin(t2 * Math.PI * 2) * 0.4,
        heartRate: 100 + Math.sin(t2 * Math.PI * 3 + 1) * 12,
        fluid: 280 + Math.sin(t2 * Math.PI * 2.5 + 0.5) * 60,
        temp: 38.2 + Math.sin(t2 * Math.PI * 1.8 + 0.3) * 0.4,
      };
    });
    return (
      <div className="w-full space-y-4 pb-[200px] sm:pb-0" data-tour="trends-chart">
        {/* Ghost stat cards */}
        <div className="grid grid-cols-2 gap-4 pointer-events-none select-none">
          {[
            { icon: <HeartPulse size={16} />, label: t('trends.avgHeartRate'), value: '104 bpm', color: 'text-rose-400' },
            { icon: <Scale size={16} />, label: t('trends.avgWeight'), value: '4.4 kg', color: 'text-blue-400' },
            { icon: <Droplets size={16} />, label: t('trends.avgWaterIntake'), value: '310 ml', color: 'text-cyan-400' },
            { icon: <Thermometer size={16} />, label: t('trends.avgTemperature'), value: '38.3°C', color: 'text-orange-400' },
          ].map(({ icon, label, value, color }, i) => (
            <div key={i} className="p-4 rounded-xl bg-white border border-gray-100 opacity-40">
              <div className={`flex items-center gap-2 mb-2 text-xs font-medium uppercase ${color}`}>
                {icon}<span className="text-text-secondary">{label}</span>
              </div>
              <p className="text-xl font-bold text-text-primary">{value}</p>
            </div>
          ))}
        </div>

        {/* Ghost weight chart */}
        <div className="relative p-4 rounded-xl bg-white border border-gray-100 pointer-events-none select-none opacity-40">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="text-blue-500" size={20} />
            <span className="text-sm font-semibold text-text-secondary">{t('trends.weightTrend')}</span>
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={ghostData}>
                <defs>
                  <linearGradient id="ghostWeight" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={false} stroke="#d1d5db" />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} stroke="#d1d5db" />
                <Area type="monotone" dataKey="weight" stroke="#3b82f6" strokeWidth={2.5} fill="url(#ghostWeight)" dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Ghost HR chart */}
        <div className="relative p-4 rounded-xl bg-white border border-gray-100 pointer-events-none select-none opacity-40">
          <div className="flex items-center gap-2 mb-4">
            <HeartPulse className="text-rose-500" size={20} />
            <span className="text-sm font-semibold text-text-secondary">{t('trends.heartRateTrend')}</span>
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={ghostData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={false} stroke="#d1d5db" />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} stroke="#d1d5db" />
                <Line type="monotone" dataKey="heartRate" stroke="#f43f5e" strokeWidth={2.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Ghost fluid chart */}
        <div className="relative p-4 rounded-xl bg-white border border-gray-100 pointer-events-none select-none opacity-40">
          <div className="flex items-center gap-2 mb-4">
            <Droplets className="text-cyan-500" size={20} />
            <span className="text-sm font-semibold text-text-secondary">{t('trends.fluidIntakeTrend')}</span>
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={ghostData}>
                <defs>
                  <linearGradient id="ghostFluid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={false} stroke="#d1d5db" />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} stroke="#d1d5db" />
                <Area type="monotone" dataKey="fluid" stroke="#06b6d4" strokeWidth={2.5} fill="url(#ghostFluid)" dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Sticky "no data" callout */}
        <div className="sticky bottom-20 sm:bottom-4 mx-auto w-fit px-6 py-4 rounded-2xl shadow-xl border border-primary-brown/20 bg-white/95 backdrop-blur-sm flex flex-col items-center gap-1 text-center">
          <Activity className="text-primary-brown mb-1" size={28} />
          <h3 className="font-black text-base text-text-primary">{t('trends.noDataTitle')}</h3>
          <p className="text-sm text-text-secondary max-w-[220px]">{t('trends.noDataBody')}</p>
        </div>
      </div>
    );
  }

  // ── Chart rendering helpers ───────────────────────────────────────────────

  const commonChartProps = {
    cartesianGrid: <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />,
    xAxis: (
      <XAxis
        dataKey="date"
        tick={{ fontSize: 12 }}
        tickFormatter={formatChartTick}
        stroke="#6b7280"
      />
    ),
    tooltipStyle: {
      contentStyle: { backgroundColor: '#ffffff', border: '1px solid #e5e7eb' },
      labelStyle: { color: '#000' },
      labelFormatter: (label: string) => formatDateByPreference(label, dateFormat),
    },
  };

  function renderWeightChart() {
    return (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-4 text-text-primary">
          <Scale className="text-blue-500" size={20} />
          <h3 className="font-semibold text-sm">{t('trends.weightTrend')}</h3>
        </div>
        <div className="h-72">
          {chartsReady && (
            <ResponsiveContainer width="100%" height="100%" minWidth={100}>
              <AreaChart data={filteredData}>
                <defs>
                  <linearGradient id="colorWeight" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                {commonChartProps.cartesianGrid}
                {commonChartProps.xAxis}
                <YAxis domain={['auto', 'auto']} stroke="#6b7280" />
                <Tooltip
                  {...commonChartProps.tooltipStyle}
                  formatter={(value) => [formatWeight(value as number, isMetric), t('trends.tooltipWeight')]}
                />
                <Area
                  type="monotone"
                  dataKey="weight"
                  stroke="#3b82f6"
                  fillOpacity={1}
                  fill="url(#colorWeight)"
                  animationDuration={400}
                  animationEasing="ease-out"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  }

  function renderHRChart() {
    return (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-4 text-text-primary">
          <HeartPulse className="text-rose-500" size={20} />
          <h3 className="font-semibold text-sm">{t('trends.heartRateTrend')}</h3>
        </div>
        <div className="h-72">
          {chartsReady && (
            <ResponsiveContainer width="100%" height="100%" minWidth={100}>
              <LineChart data={filteredData}>
                {commonChartProps.cartesianGrid}
                {commonChartProps.xAxis}
                <YAxis domain={['auto', 'auto']} stroke="#6b7280" />
                <Tooltip
                  {...commonChartProps.tooltipStyle}
                  formatter={(value) => [`${value} bpm`, t('trends.tooltipHeartRate')]}
                />
                <Line type="monotone" dataKey="heartRate" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3 }} animationDuration={400} animationEasing="ease-out" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  }

  function renderFluidChart() {
    return (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-4 text-text-primary">
          <Droplets className="text-cyan-500" size={20} />
          <h3 className="font-semibold text-sm">{t('trends.fluidIntakeTrend')}</h3>
        </div>
        <div className="h-72">
          {chartsReady && (
            <ResponsiveContainer width="100%" height="100%" minWidth={100}>
            <AreaChart data={filteredData}>
              <defs>
                <linearGradient id="colorFluid" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                </linearGradient>
              </defs>
              {commonChartProps.cartesianGrid}
              {commonChartProps.xAxis}
              <YAxis stroke="#6b7280" tickFormatter={(v) => Math.round(v).toString()} />
              <Tooltip
                {...commonChartProps.tooltipStyle}
                formatter={(value) => [formatFluid(Math.round(value as number), isMetric), t('trends.tooltipFluid')]}
              />
              <Area type="monotone" dataKey="fluid" stroke="#06b6d4" fillOpacity={1} fill="url(#colorFluid)" animationDuration={400} animationEasing="ease-out" />
            </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  }

  function renderRRChart() {
    return (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-4 text-text-primary">
          <Wind className="text-indigo-500" size={20} />
          <h3 className="font-semibold text-sm">{t('trends.respRateTrend')}</h3>
        </div>
        <div className="h-72">
          {chartsReady && (
            <ResponsiveContainer width="100%" height="100%" minWidth={100}>
              <LineChart data={filteredData}>
                {commonChartProps.cartesianGrid}
                {commonChartProps.xAxis}
                <YAxis domain={['auto', 'auto']} stroke="#6b7280" />
                <Tooltip
                  {...commonChartProps.tooltipStyle}
                  formatter={(value) => [`${value} bpm`, t('trends.tooltipRespRate')]}
                />
                <Line type="monotone" dataKey="respRate" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} animationDuration={400} animationEasing="ease-out" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  }

  function renderTempChart() {
    return (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-4 text-text-primary">
          <Thermometer className="text-orange-500" size={20} />
          <h3 className="font-semibold text-sm">{t('trends.tempTrend')}</h3>
        </div>
        <div className="h-72">
          {chartsReady && (
            <ResponsiveContainer width="100%" height="100%" minWidth={100}>
              <LineChart data={filteredData}>
                {commonChartProps.cartesianGrid}
                {commonChartProps.xAxis}
                <YAxis domain={['auto', 'auto']} stroke="#6b7280" />
                <Tooltip
                  {...commonChartProps.tooltipStyle}
                  formatter={(value) => [formatTemp(value as number, isMetric), t('trends.tooltipTemp')]}
                />
                <Line type="monotone" dataKey="temp" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} animationDuration={400} animationEasing="ease-out" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  }

  function renderMucousMembraneChart() {
    return (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-4 text-text-primary">
          <MucousMembraneIcon className="text-accent-orange" width={18} height={18} />
          <h3 className="font-semibold text-sm">{t('trends.mucousMembraneTrend')}</h3>
        </div>
        <div className="h-72">
          {chartsReady && (
            mucousMembraneData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={100}>
                <PieChart>
                  <Pie
                    data={mucousMembraneData}
                    dataKey="value"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={54}
                    outerRadius={94}
                    paddingAngle={3}
                    label={({ percent }) => `${Math.round((percent ?? 0) * 100)}%`}
                    labelLine={false}
                    animationDuration={400}
                    animationEasing="ease-out"
                  >
                    {mucousMembraneData.map(entry => (
                      <Cell key={entry.key} fill={entry.fill} stroke={entry.key === 'white' ? '#d1d5db' : entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
                    labelStyle={{ color: '#000' }}
                    formatter={(value: number, _name, item) => {
                      const daysLabel = value === 1 ? t('trends.day') : t('trends.days');
                      return [`${value} ${daysLabel}`, item.payload.label];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 text-center text-sm text-text-secondary">
                {t('trends.noMucousMembraneData')}
              </div>
            )
          )}
        </div>
        {mucousMembraneData.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {mucousMembraneData.map(entry => (
              <div key={entry.key} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2 text-xs font-medium text-text-secondary">
                  <span
                    className="h-2.5 w-2.5 rounded-full border border-slate-200"
                    style={{ backgroundColor: entry.fill }}
                  />
                  <span>{entry.label}</span>
                </div>
                <p className="mt-1 text-sm font-bold text-text-primary">{entry.value}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderFoodIntakeChart() {
    return (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-4 text-text-primary">
          <Utensils className="text-amber-500" size={20} />
          <h3 className="font-semibold text-sm">{t('trends.foodIntakeTrend')}</h3>
        </div>
        <div className="h-72">
          {chartsReady && (
            <ResponsiveContainer width="100%" height="100%" minWidth={100}>
              <AreaChart data={filteredData}>
                <defs>
                  <linearGradient id="colorFood" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                {commonChartProps.cartesianGrid}
                {commonChartProps.xAxis}
                <YAxis stroke="#6b7280" tickFormatter={(v) => isMetric ? `${(v / 1000).toFixed(1)}` : `${(v / 28349.5).toFixed(1)}`} />
                <Tooltip
                  {...commonChartProps.tooltipStyle}
                  formatter={(value) => [
                    isMetric
                      ? `${((value as number) / 1000).toFixed(1)} g`
                      : `${((value as number) / 28349.5).toFixed(2)} oz`,
                    t('trends.tooltipFood'),
                  ]}
                />
                <Area type="monotone" dataKey="food" stroke="#f59e0b" fillOpacity={1} fill="url(#colorFood)" animationDuration={400} animationEasing="ease-out" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  }

  // IDs that render as compact stat cards (vs full-height charts)
  const AVG_IDS = new Set<WidgetId>([
    'avgHeartRate', 'avgRespRate', 'avgWeight', 'avgMucousMembrane', 'avgWaterIntake', 'avgTemperature', 'avgFoodIntake',
  ]);

  // Unified renderer map — one entry per widget ID
  const allRenderers: Record<WidgetId, () => React.ReactNode> = {
    // ── Avg stat cards ─────────────────────────────────────────────────────
    avgHeartRate: () => (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-2 text-xs font-medium uppercase text-text-secondary">
          <HeartPulse size={16} />
          <span>{t('trends.avgHeartRate')}</span>
        </div>
        <p className="text-xl font-bold text-text-primary">{avgHR != null ? `${avgHR} bpm` : '--'}</p>
      </div>
    ),
    avgRespRate: () => (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-2 text-xs font-medium uppercase text-text-secondary">
          <Wind size={16} />
          <span>{t('trends.avgRespRate')}</span>
        </div>
        <p className="text-xl font-bold text-text-primary">{avgRR != null ? `${avgRR} bpm` : '--'}</p>
      </div>
    ),
    avgWeight: () => (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-2 text-xs font-medium uppercase text-text-secondary">
          <Scale size={16} />
          <span>{t('trends.avgWeight')}</span>
        </div>
        <p className="text-xl font-bold text-text-primary">{avgWeight != null ? formatWeight(avgWeight, isMetric) : '--'}</p>
      </div>
    ),
    avgMucousMembrane: () => (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-2 text-xs font-medium uppercase text-text-secondary">
          <MucousMembraneIcon width={14} height={14} className="text-accent-orange" />
          <span>{t('trends.avgMucousMembrane')}</span>
        </div>
        <p className="text-xl font-bold text-text-primary">
          {avgMucousMembrane != null ? t(MUCUS_MEMBRANE_META[avgMucousMembrane].labelKey) : '--'}
        </p>
      </div>
    ),
    avgWaterIntake: () => (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-2 text-xs font-medium uppercase text-text-secondary">
          <Droplets size={16} />
          <span>{t('trends.avgWaterIntake')}</span>
        </div>
        <p className="text-xl font-bold text-text-primary">{avgFluid != null ? formatFluid(Math.round(avgFluid), isMetric) : '--'}</p>
      </div>
    ),
    avgTemperature: () => (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-2 text-xs font-medium uppercase text-text-secondary">
          <Thermometer size={16} />
          <span>{t('trends.avgTemperature')}</span>
        </div>
        <p className="text-xl font-bold text-text-primary">{avgTemp != null ? formatTemp(avgTemp, isMetric) : '--'}</p>
      </div>
    ),
    avgFoodIntake: () => (
      <div className="p-4 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-2 text-xs font-medium uppercase text-text-secondary">
          <Utensils size={16} />
          <span>{t('trends.avgFoodIntake')}</span>
        </div>
        <p className="text-xl font-bold text-text-primary">
          {avgFood != null
            ? isMetric
              ? `${(avgFood / 1000).toFixed(1)} g`
              : `${(avgFood / 28349.5).toFixed(2)} oz`
            : '--'}
        </p>
      </div>
    ),
    // ── Charts ────────────────────────────────────────────────────────────
    weight: renderWeightChart,
    heartRate: renderHRChart,
    respRate: renderRRChart,
    mucousMembrane: renderMucousMembraneChart,
    fluid: renderFluidChart,
    temp: renderTempChart,
    foodIntake: renderFoodIntakeChart,
  };

  // Build ordered render groups: consecutive avg cards collapse into a 2-col grid row
  type RenderGroup =
    | { type: 'avg'; items: WidgetId[] }
    | { type: 'chart'; id: WidgetId };

  const renderGroups = widgets
    .filter(w => w.visible)
    .reduce<RenderGroup[]>((groups, w) => {
      if (AVG_IDS.has(w.id)) {
        const last = groups[groups.length - 1];
        if (last?.type === 'avg') {
          last.items.push(w.id);
        } else {
          groups.push({ type: 'avg', items: [w.id] });
        }
      } else {
        groups.push({ type: 'chart', id: w.id });
      }
      return groups;
    }, []);

  // ── Customise bottom sheet ────────────────────────────────────────────────

  const widgetLabels: Record<WidgetId, string> = {
    avgHeartRate: t('trends.avgHeartRate'),
    avgRespRate: t('trends.avgRespRate'),
    weight: t('trends.weightTrend'),
    heartRate: t('trends.heartRateTrend'),
    respRate: t('trends.respRateTrend'),
    mucousMembrane: t('trends.mucousMembraneTrend'),
    avgWeight: t('trends.avgWeight'),
    avgMucousMembrane: t('trends.avgMucousMembrane'),
    avgWaterIntake: t('trends.avgWaterIntake'),
    avgTemperature: t('trends.avgTemperature'),
    temp: t('trends.tempTrend'),
    fluid: t('trends.fluidIntakeTrend'),
    foodIntake: t('trends.foodIntakeTrend'),
    avgFoodIntake: t('trends.avgFoodIntake'),
  };

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="w-full space-y-4 bg-white pb-[200px] sm:pb-0" data-tour="trends-chart">

      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Customise button (Lifetime only) */}
          {isCustomisable && (
            <button
              onClick={() => setShowCustomise(true)}
              className="flex items-center gap-1.5 text-xs font-medium text-primary-brown border border-primary-brown/30 rounded-xl px-3 py-1.5 hover:bg-blue-50 transition-colors"
            >
              <SlidersHorizontal size={14} />
              {t('trends.customise')}
            </button>
          )}
        </div>
        {/* Range picker — right side */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400 font-medium">{t('trends.showLast')}</span>
          <Dropdown
            trigger={
              <span className="flex items-center gap-1.5 px-3 py-2 border rounded-xl border-gray-300 bg-white text-sm text-text-primary">
                {range}
                <ChevronDown size={14} className="text-text-secondary" />
              </span>
            }
            title={t('trends.showLast')}
            items={[
              { label: '7d',  onClick: () => setRange('7d') },
              { label: '14d', onClick: () => setRange('14d') },
              { label: '30d', onClick: () => setRange('30d') },
            ]}
            triggerClassName="flex"
          />
        </div>
      </div>

      {/* Insufficient data warning */}
      {filteredUniqueDates < 3 && (
        <div className="p-4 rounded-xl border flex items-start gap-3 bg-yellow-50 border-yellow-200">
          <Activity size={20} className="flex-shrink-0 mt-0.5 text-yellow-600" />
          <div>
            <h3 className="font-bold mb-1 text-yellow-800">{t('trends.moreDataNeeded')}</h3>
            <p className="text-sm text-yellow-700">
              {t('trends.moreDataNeededBody')} ({filteredUniqueDates}{' '}
              {filteredUniqueDates !== 1 ? t('trends.days') : t('trends.day')})
            </p>
          </div>
        </div>
      )}

      {/* All widgets — rendered in list order; consecutive avg cards share a 2-col grid row */}
      {filteredUniqueDates >= 3 && (
        <>
          {renderGroups.map((group, i) =>
            group.type === 'avg' ? (
              <div key={i} className="grid grid-cols-2 gap-4">
                {group.items.map(id => (
                  <React.Fragment key={id}>{allRenderers[id]?.()}</React.Fragment>
                ))}
              </div>
            ) : (
              <React.Fragment key={group.id}>{allRenderers[group.id]?.()}</React.Fragment>
            )
          )}
        </>
      )}

      {/* Customise bottom sheet (Lifetime) */}
      {showCustomise && isCustomisable && (
        <>
          {/* Scrim */}
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setShowCustomise(false)}
          />
          {/* Sheet */}
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100">
              <h3 className="font-black text-[#0d2744] text-base">{t('trends.customise')}</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={resetToDefaults}
                  className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-secondary"
                >
                  <RotateCcw size={13} />
                  {t('trends.resetLayout')}
                </button>
                <button
                  onClick={() => setShowCustomise(false)}
                  className="p-1.5 rounded-full hover:bg-gray-100"
                >
                  <X size={18} className="text-text-secondary" />
                </button>
              </div>
            </div>
            <ul className="px-4 py-3 space-y-1 pb-10">
              {widgets.filter(w => widgetLabels[w.id] != null).map((w, idx, filteredWidgets) => (
                <li
                  key={w.id}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50"
                >
                  {/* Visibility toggle */}
                  <button
                    onClick={() => toggleWidget(w.id)}
                    className={cn(
                      'p-1.5 rounded-lg transition-colors',
                      w.visible ? 'text-primary-brown' : 'text-gray-300',
                    )}
                  >
                    {w.visible ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>

                  {/* Label */}
                  <span className={cn('flex-1 text-sm font-medium', !w.visible && 'text-gray-400')}>
                    {widgetLabels[w.id]}
                  </span>

                  {/* Reorder */}
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveWidget(w.id, 'up')}
                      disabled={idx === 0}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-20"
                    >
                      <ChevronUp size={16} className="text-text-secondary" />
                    </button>
                    <button
                      onClick={() => moveWidget(w.id, 'down')}
                      disabled={idx === filteredWidgets.length - 1}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-20"
                    >
                      <ChevronDown size={16} className="text-text-secondary" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
